import * as React from 'react';

import {
  Chart,
  Content,
  DataGrid,
  DataGridColumn,
  EntityDetails,
  Footer,
  humanReadableCurrency,
  humanReadableDateRange,
  humanReadableNumber,
  Pagination,
} from '@rematter/ui-web';
import { usePreferredLanguage } from '@uidotdev/usehooks';
import { useAtom, useAtomValue } from 'jotai';
import camelCase from 'lodash/camelCase';
import groupBy from 'lodash/groupBy';
import sumBy from 'lodash/sumBy';
import upperFirst from 'lodash/upperFirst';

import { UsageMetricsRecord } from '../../_graphql/querySystemUsageMetrics';
import * as state from '../../_state/usageOrganizationDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { adminRoutes } from '../../routes';

import { UsageFilterBar } from './UsageFilterBar';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const UsageDetailsTab = () => {
  const t = useTranslations();
  const [limit = 25, setLimit] = useAtom(state.$detailLimit);
  const [offset, setOffset] = useAtom(state.$detailOffset);

  const preferredLocale = usePreferredLanguage();

  const {
    data: results,
    isLoading,
    error,
  } = useAtomValue(state.$remoteOrganizationUsageMetrics);

  const metrics = React.useMemo(
    () => results?.entries ?? [],
    [results?.entries],
  );
  const total = results?.total ?? 0;

  // Calculate summary statistics
  const summary = React.useMemo(() => {
    const totalTokens = sumBy(metrics, 'totalTokens');
    const totalCost = sumBy(metrics, (m) => m.totalCost.amount);
    const currencyCode = metrics[0]?.totalCost.currencyCode ?? 'USD';

    // Find most used model
    const modelUsage = groupBy(metrics, 'model');
    const mostUsedModel =
      Object.entries(modelUsage).sort(
        (a, b) => sumBy(b[1], 'totalTokens') - sumBy(a[1], 'totalTokens'),
      )[0]?.[0] ?? 'N/A';

    return {
      totalTokens,
      totalCost,
      currencyCode,
      mostUsedModel,
    };
  }, [metrics]);

  // Group by provider for pie chart
  const providerData = React.useMemo(() => {
    const grouped = groupBy(metrics, 'modelProvider');
    return Object.entries(grouped).map(([provider, records]) => ({
      name: upperFirst(camelCase(provider)),
      value: sumBy(records, 'totalTokens'),
      cost: sumBy(records, (r) => r.totalCost.amount),
    }));
  }, [metrics]);

  // Group by model for bar chart
  const modelData = React.useMemo(() => {
    const grouped = groupBy(metrics, 'model');
    return Object.entries(grouped)
      .map(([model, records]) => ({
        name: model,
        tokens: sumBy(records, 'totalTokens'),
        cost: sumBy(records, (r) => r.totalCost.amount),
        records: sumBy(records, 'recordCount'),
      }))
      .sort((a, b) => b.tokens - a.tokens)
      .slice(0, 10); // Top 10 models
  }, [metrics]);

  const columns: DataGridColumn<UsageMetricsRecord>[] = React.useMemo(
    () => [
      {
        key: 'modelProvider',
        header: t.aiManagement.usage.header.modelProvider,
        width: 120,
        minWidth: 100,
        cell: (cell) => (
          <div>{upperFirst(camelCase(cell.row.modelProvider))}</div>
        ),
      },
      {
        key: 'model',
        header: t.aiManagement.usage.header.model,
        width: 200,
        minWidth: 150,
        cell: (cell) => (
          <div className="font-mono text-sm">{cell.row.model}</div>
        ),
      },
      {
        key: 'totalTokens',
        header: t.aiManagement.usage.header.totalTokens,
        width: 120,
        minWidth: 100,
        align: 'right',
        cell: (cell) => (
          <div>
            {humanReadableNumber(cell.row.totalTokens, {
              locale: preferredLocale,
            })}
          </div>
        ),
      },
      {
        key: 'totalCost',
        header: t.aiManagement.usage.header.totalCost,
        width: 120,
        minWidth: 100,
        align: 'right',
        cell: (cell) => (
          <div>
            {humanReadableCurrency(cell.row.totalCost.amount, {
              locale: preferredLocale,
              currencyCode: cell.row.totalCost.currencyCode,
            })}
          </div>
        ),
      },
      {
        key: 'recordCount',
        header: t.aiManagement.usage.header.recordCount,
        width: 100,
        minWidth: 80,
        align: 'right',
        cell: (cell) => (
          <div>
            {humanReadableNumber(cell.row.recordCount, {
              locale: preferredLocale,
            })}
          </div>
        ),
      },
      {
        key: 'period',
        header: t.aiManagement.usage.header.period,
        width: 200,
        minWidth: 150,
        cell: (cell) => (
          <div className="text-sm">
            {humanReadableDateRange(cell.row.periodStart, cell.row.periodEnd, {
              locale: preferredLocale,
            })}
          </div>
        ),
      },
    ],
    [t, preferredLocale],
  );

  const handlePaginationChange = ({
    limit: newLimit,
    offset: newOffset,
  }: {
    limit: number;
    offset: number;
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  return (
    <div className="@UsageDetailsTab flex flex-col">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 border-b border-neutral/10 p-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-neutral/10 bg-space p-4">
          <div className="text-sm text-neutral/60">
            {t.aiManagement.usage.organizationDetail.summary.totalTokens}
          </div>
          <div className="mt-1 text-2xl font-semibold text-neutral/90">
            {humanReadableNumber(summary.totalTokens, {
              locale: preferredLocale,
            })}
          </div>
        </div>
        <div className="rounded-lg border border-neutral/10 bg-space p-4">
          <div className="text-sm text-neutral/60">
            {t.aiManagement.usage.organizationDetail.summary.totalCost}
          </div>
          <div className="mt-1 text-2xl font-semibold text-neutral/90">
            {humanReadableCurrency(summary.totalCost, {
              locale: preferredLocale,
              currencyCode: summary.currencyCode,
            })}
          </div>
        </div>
        <div className="rounded-lg border border-neutral/10 bg-space p-4">
          <div className="text-sm text-neutral/60">
            {t.aiManagement.usage.organizationDetail.summary.mostUsedModel}
          </div>
          <div className="mt-1 text-lg font-semibold text-neutral/90">
            {summary.mostUsedModel}
          </div>
        </div>
        <div className="rounded-lg border border-neutral/10 bg-space p-4">
          <div className="text-sm text-neutral/60">
            {t.aiManagement.usage.header.recordCount}
          </div>
          <div className="mt-1 text-2xl font-semibold text-neutral/90">
            {humanReadableNumber(total, { locale: preferredLocale })}
          </div>
        </div>
      </div>

      {/* Charts Section */}
      {metrics.length > 0 && (
        <div className="grid grid-cols-1 gap-4 border-b border-neutral/10 p-4 lg:grid-cols-2">
          {/* Provider Breakdown */}
          <div className="rounded-lg border border-neutral/10 bg-space p-4">
            <h3 className="mb-4 text-lg font-semibold text-neutral/90">
              Usage by Provider
            </h3>
            <Chart type="pie" data={providerData} yKey="value" />
          </div>

          {/* Model Breakdown */}
          <div className="rounded-lg border border-neutral/10 bg-space p-4">
            <h3 className="mb-4 text-lg font-semibold text-neutral/90">
              Top Models by Token Usage
            </h3>
            <Chart type="bar" data={modelData} xKey="name" yKey="tokens" />
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <UsageFilterBar />

      {/* Detailed Records Table */}
      <Content className="border-t border-neutral/10">
        <div className="p-4">
          <h3 className="mb-4 text-lg font-semibold text-neutral/90">
            {t.aiManagement.usage.organizationDetail.tabs.details}
          </h3>
        </div>
        <DataGrid
          columns={columns}
          dataSource={metrics}
          fullWidth
          isLoading={isLoading}
          hasError={!!error}
          emptyPanelProps={{
            title: t.aiManagement.usage.empty.title,
            description: t.aiManagement.usage.empty.description,
          }}
        />
      </Content>

      {total > limit && (
        <Footer>
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        </Footer>
      )}
    </div>
  );
};

export const AIUsageOrganizationDetailView: React.FC = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  const {
    data: results,
    isLoading,
    error,
  } = useAtomValue(state.$remoteOrganizationUsageMetrics);

  const metrics = results?.entries ?? [];
  const organizationName = metrics[0]?.organization.name ?? 'Unknown';

  const handleBackToList = React.useCallback(() => {
    navigate(adminRoutes.aiUsage);
  }, [navigate]);

  if (isLoading && metrics.length === 0) {
    return (
      <div className="@AIUsageOrganizationDetailView flex h-full items-center justify-center">
        <div className="text-neutral/60">
          {t.aiManagement.loading.usageData}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="@AIUsageOrganizationDetailView flex h-full items-center justify-center">
        <div className="text-failure">{error.message}</div>
      </div>
    );
  }

  return (
    <div className="@AIUsageOrganizationDetailView flex h-full flex-col">
      <EntityDetails
        className="@UsageDetailsView h-full"
        onBackPressed={handleBackToList}
        primaryTitle={organizationName}
        tabs={{
          overview: {
            title: t.aiManagement.usage.organizationDetail.tabs.details,
            content: UsageDetailsTab,
          },
        }}
        currentTab="overview"
        onChangeTab={() => {
          // Single tab - no tab change needed
        }}
      />
    </div>
  );
};
